import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import {
	IconArrowRight,
	IconArrowLeft,
	IconUserPlus,
	IconInfoCircle,
} from "@tabler/icons-react";
import {
	api,
	type Gender,
	type MeResponse,
	type ProfileInput,
} from "../lib/api.ts";
import { isProfileComplete } from "../lib/profile.ts";
import {
	Button,
	Card,
	ErrorBlock,
	InfoBlock,
	PageSkeleton,
} from "../components/Ui.tsx";
import { toast } from "../lib/toast.tsx";

const GENDERS: { value: Gender; label: string; emoji: string }[] = [
	{ value: "male", label: "Laki-laki", emoji: "👨" },
	{ value: "female", label: "Perempuan", emoji: "👩" },
];

function getZodiac(birthDate: string): { emoji: string; label: string } | null {
	if (!birthDate) return null;
	const d = new Date(birthDate);
	const month = d.getMonth() + 1;
	const day = d.getDate();

	const zodiacs: Array<{ emoji: string; label: string; startMonth: number; startDay: number; endMonth: number; endDay: number }> = [
		{ emoji: "♒", label: "Aquarius", startMonth: 1, startDay: 20, endMonth: 2, endDay: 18 },
		{ emoji: "♓", label: "Pisces", startMonth: 2, startDay: 19, endMonth: 3, endDay: 20 },
		{ emoji: "♈", label: "Aries", startMonth: 3, startDay: 21, endMonth: 4, endDay: 19 },
		{ emoji: "♉", label: "Taurus", startMonth: 4, startDay: 20, endMonth: 5, endDay: 20 },
		{ emoji: "♊", label: "Gemini", startMonth: 5, startDay: 21, endMonth: 6, endDay: 20 },
		{ emoji: "♋", label: "Cancer", startMonth: 6, startDay: 21, endMonth: 7, endDay: 22 },
		{ emoji: "♌", label: "Leo", startMonth: 7, startDay: 23, endMonth: 8, endDay: 22 },
		{ emoji: "♍", label: "Virgo", startMonth: 8, startDay: 23, endMonth: 9, endDay: 22 },
		{ emoji: "♎", label: "Libra", startMonth: 9, startDay: 23, endMonth: 10, endDay: 22 },
		{ emoji: "♏", label: "Scorpio", startMonth: 10, startDay: 23, endMonth: 11, endDay: 21 },
		{ emoji: "♐", label: "Sagittarius", startMonth: 11, startDay: 22, endMonth: 12, endDay: 21 },
		{ emoji: "♑", label: "Capricorn", startMonth: 12, startDay: 22, endMonth: 1, endDay: 19 },
	];

	for (const z of zodiacs) {
		if (z.startMonth === z.endMonth) {
			if (month === z.startMonth && day >= z.startDay && day <= z.endDay) return { emoji: z.emoji, label: z.label };
		} else if (month === z.startMonth && day >= z.startDay) {
			return { emoji: z.emoji, label: z.label };
		} else if (month === z.endMonth && day <= z.endDay) {
			return { emoji: z.emoji, label: z.label };
		}
	}
	return { emoji: "♑", label: "Capricorn" };
}

function getAge(birthDate: string): number | null {
	if (!birthDate) return null;
	const today = new Date();
	const birth = new Date(birthDate);
	let age = today.getFullYear() - birth.getFullYear();
	const m = today.getMonth() - birth.getMonth();
	if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
		age--;
	}
	return age;
}

export default function Onboarding() {
	const { getToken, isSignedIn, session } = useAuth();
	const navigate = useNavigate();
	const [me, setMe] = useState<MeResponse | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [step, setStep] = useState<"gender" | "birthdate" | "couple">("gender");
	const [gender, setGender] = useState<Gender | "">("");
	const [birthDate, setBirthDate] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [joinModalOpen, setJoinModalOpen] = useState(false);
	const [joinCode, setJoinCode] = useState("");
	const [joinStep, setJoinStep] = useState<"input" | "confirm">("input");
	const [partnerPreview, setPartnerPreview] = useState<any>(null);
	const [joinLoading, setJoinLoading] = useState(false);
	const [joinError, setJoinError] = useState<string | null>(null);
	const [showCode, setShowCode] = useState(false);
	const [generatedCode, setGeneratedCode] = useState<string | null>(null);
	const [generating, setGenerating] = useState(false);
	const authName = session.data?.user?.name ?? null;

	const zodiac = getZodiac(birthDate);
	const age = getAge(birthDate);

	async function load() {
		try {
			const token = await getToken();
			if (!token) return;
			const res = await api.me(token);
			setMe(res);
			const profileComplete = isProfileComplete(res.profile);
			if (profileComplete) {
				navigate("/dashboard", { replace: true });
				return;
			}
			// Resume from last incomplete step
			if (res.profile?.gender) {
				setGender(res.profile.gender as Gender);
			}
			if (res.profile?.birthDate) {
				setBirthDate(res.profile.birthDate);
			}
			if (res.profile?.gender && res.profile?.birthDate) {
				setStep("couple");
			} else if (res.profile?.gender) {
				setStep("birthdate");
			} else {
				setStep("gender");
			}
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		if (!isSignedIn) return;
		const id = setTimeout(() => void load(), 0);
		return () => clearTimeout(id);
	}, [isSignedIn]);

	async function saveProfile(g: Gender, bd: string) {
		if (!g || !bd) {
			toast.error("Data profil belum lengkap");
			return;
		}
		setSubmitting(true);
		try {
			const token = await getToken();
			if (!token) throw new Error("Tidak ada token");
			const body: ProfileInput = {
				nickname: authName?.trim() || "User",
				gender: g,
				birthDate: bd,
				adultConsent: false,
			};
			await api.updateProfile(token, body);
		} catch (e) {
			toast.error("Gagal menyimpan profil");
			throw e;
		} finally {
			setSubmitting(false);
		}
	}

	function handleGenderNext() {
		if (!gender) return;
		setStep("birthdate");
	}

	async function handleBirthdateNext() {
		if (!birthDate || !gender) return;
		await saveProfile(gender as Gender, birthDate);
		setStep("couple");
	}

	function handleSkip() {
		navigate("/dashboard", { replace: true });
	}

	async function handleJoinLookup() {
		if (!joinCode.trim()) return;
		setJoinLoading(true);
		setJoinError(null);
		try {
			const token = await getToken();
			if (!token) return;
			// Look up the couple by code to preview partner info
			const res = await fetch(`/api/couple/lookup?code=${encodeURIComponent(joinCode.trim().toUpperCase())}`, {
				headers: { Authorization: `Bearer ${token}` },
			});
			if (!res.ok) {
				setJoinError("Kode tidak ditemukan. Cek ulang kodenya.");
				setJoinLoading(false);
				return;
			}
			const data = await res.json();
			setPartnerPreview(data);
			setJoinStep("confirm");
		} catch {
			setJoinError("Gagal memeriksa kode.");
		} finally {
			setJoinLoading(false);
		}
	}

	async function handleJoinConfirm() {
		setJoinLoading(true);
		setJoinError(null);
		try {
			const token = await getToken();
			if (!token) return;
			await api.joinCouple(token, joinCode.trim().toUpperCase());
			toast.success("Berhasil bergabung!");
			setJoinModalOpen(false);
			void load();
		} catch (e) {
			const msg = e instanceof Error ? e.message : String(e);
			const hints: Record<string, string> = {
				CODE_NOT_FOUND: "Kode tidak ditemukan.",
				CODE_EXPIRED: "Kode sudah kedaluwarsa.",
				CODE_ALREADY_USED: "Kode sudah dipakai.",
				CANNOT_JOIN_OWN_CODE: "Tidak bisa gabung kode sendiri.",
			};
			setJoinError(hints[msg] ?? msg);
		} finally {
			setJoinLoading(false);
		}
	}

	async function handleGenerateCode() {
		setGenerating(true);
		try {
			const token = await getToken();
			if (!token) return;
			const res = await api.createCouple(token, { nickname: authName ?? undefined });
			setGeneratedCode(res.code);
			setShowCode(true);
			void load();
		} catch (e) {
			toast.error("Gagal membuat kode");
		} finally {
			setGenerating(false);
		}
	}

	if (loading) return <PageSkeleton variant="profile" />;
	if (error) return <ErrorBlock message={error} />;
	if (!me) return <ErrorBlock message="Tidak ada data" />;

	const hasCouple = me.couple !== null;
	const paired = me.couple?.paired === true;

	if (hasCouple && paired) {
		navigate("/dashboard", { replace: true });
		return null;
	}

	const steps = [
		{ id: "gender", label: "Jenis Kelamin" },
		{ id: "birthdate", label: "Tanggal Lahir" },
		{ id: "couple", label: "Pasangan" },
	];
	const currentIdx = steps.findIndex(s => s.id === step);

	return (
		<div style={{ minHeight: "100vh", background: "var(--color-bg)", display: "flex", flexDirection: "column" }}>
			{/* Progress bar */}
			<div style={{ padding: "0.75rem 1rem", display: "flex", gap: "0.25rem" }}>
				{steps.map((s, i) => (
					<div key={s.id} style={{
						flex: 1, height: 4, borderRadius: 999,
						background: i <= currentIdx ? "var(--color-primary)" : "var(--color-border)",
						transition: "background 200ms ease",
					}} />
				))}
			</div>

			<div style={{ flex: 1, display: "flex", flexDirection: "column", maxWidth: 480, margin: "0 auto", padding: "0 1rem", width: "100%" }}>
				{/* Step 1: Gender */}
				{step === "gender" && (
					<div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: "1.5rem" }}>
						<div style={{ textAlign: "center" }}>
							<h1 style={{ margin: "0 0 0.5rem", fontSize: "1.5rem", fontWeight: 800 }}>
								Jenis kelaminmu?
							</h1>
							<p style={{ margin: 0, fontSize: "0.88rem", color: "var(--color-text-muted)", lineHeight: 1.5 }}>
								Diperlukan untuk personalisasi pengalaman di Yayy.
							</p>
						</div>

						<div style={{ display: "grid", gap: "0.75rem" }}>
							{GENDERS.map((g) => (
								<button
									key={g.value}
									type="button"
									onClick={() => setGender(g.value)}
									style={{
										display: "flex",
										alignItems: "center",
										gap: "1rem",
										padding: "1rem 1.25rem",
										borderRadius: "var(--radius-lg)",
										border: gender === g.value
											? "2px solid var(--color-primary)"
											: "2px solid var(--color-border)",
										background: gender === g.value
											? "color-mix(in srgb, var(--color-primary) 8%, var(--color-surface))"
											: "var(--color-surface)",
										cursor: "pointer",
										transition: "all 150ms ease",
										textAlign: "left",
									}}
								>
									<span style={{ fontSize: "1.8rem" }}>{g.emoji}</span>
									<div>
										<div style={{ fontSize: "1rem", fontWeight: 700, color: "var(--color-text)" }}>
											{g.label}
										</div>
									</div>
								</button>
							))}
						</div>

						{gender === "female" && (
							<InfoBlock tone="info">
								<div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
									<IconInfoCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
									<span style={{ fontSize: "0.82rem", lineHeight: 1.5 }}>
										Pemilihan "Perempuan" akan mengaktifkan <strong>kalender menstruasi</strong> untuk melacak siklus dan ovulasi.
									</span>
								</div>
							</InfoBlock>
						)}

						<Button
							onClick={handleGenderNext}
							disabled={!gender}
							style={{ opacity: gender ? 1 : 0.5, display: "flex", alignItems: "center", justifyContent: "center", gap: "0.4rem" }}
						>
							Lanjut <IconArrowRight size={16} />
						</Button>
					</div>
				)}

				{/* Step 2: Birth Date */}
				{step === "birthdate" && (
					<div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: "1.5rem" }}>
						<div style={{ textAlign: "center" }}>
							<h1 style={{ margin: "0 0 0.5rem", fontSize: "1.5rem", fontWeight: 800 }}>
								Tanggal lahirmu?
							</h1>
							<p style={{ margin: 0, fontSize: "0.88rem", color: "var(--color-text-muted)", lineHeight: 1.5 }}>
								Digunakan untuk menyesuaikan pengalaman dan fitur khusus.
							</p>
						</div>

						<div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.75rem" }}>
							<input
								type="date"
								value={birthDate}
								max={new Date().toISOString().slice(0, 10)}
								onChange={(e) => setBirthDate(e.target.value)}
								style={{
									width: "100%",
									padding: "0.75rem",
									borderRadius: "var(--radius-lg)",
									border: "2px solid var(--color-border)",
									background: "var(--color-surface)",
									fontSize: "1rem",
									textAlign: "center",
									color: "var(--color-text)",
								}}
							/>

							{birthDate && zodiac && (
								<div style={{
									display: "flex",
									alignItems: "center",
									gap: "0.75rem",
									padding: "0.75rem 1rem",
									borderRadius: "var(--radius-lg)",
									background: "color-mix(in srgb, var(--color-primary) 8%, var(--color-surface))",
									border: "1px solid color-mix(in srgb, var(--color-primary) 20%, transparent)",
									width: "100%",
								}}>
									<span style={{ fontSize: "2rem" }}>{zodiac.emoji}</span>
									<div>
										<div style={{ fontSize: "1rem", fontWeight: 700, color: "var(--color-text)" }}>
											{zodiac.label}
										</div>
										{age !== null && (
											<div style={{ fontSize: "0.78rem", color: "var(--color-text-muted)" }}>
												{age} tahun
											</div>
										)}
									</div>
								</div>
							)}
						</div>

						<div style={{ display: "flex", gap: "0.5rem" }}>
							<button
								type="button"
								onClick={() => setStep("gender")}
								style={{
									flex: 1, padding: "0.75rem", borderRadius: "var(--radius-lg)",
									border: "2px solid var(--color-border)", background: "var(--color-surface)",
									cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
									gap: "0.4rem", fontSize: "0.9rem", fontWeight: 600, color: "var(--color-text)",
								}}
							>
								<IconArrowLeft size={16} /> Kembali
							</button>
							<Button
								onClick={handleBirthdateNext}
								disabled={!birthDate || submitting}
								style={{ flex: 1, opacity: birthDate && !submitting ? 1 : 0.5, display: "flex", alignItems: "center", justifyContent: "center", gap: "0.4rem" }}
							>
								{submitting ? "Menyimpan..." : "Lanjut"} {!submitting && <IconArrowRight size={16} />}
							</Button>
						</div>
					</div>
				)}

				{/* Step 3: Couple */}
				{step === "couple" && (
					<div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: "1.5rem" }}>
						<div style={{ textAlign: "center" }}>
							<button
								type="button"
								onClick={() => setStep("birthdate")}
								style={{
									display: "inline-flex", alignItems: "center", gap: "0.3rem",
									marginBottom: "0.75rem", padding: "0.3rem 0.5rem",
									borderRadius: "var(--radius-md)", border: "none",
									background: "transparent", cursor: "pointer",
									fontSize: "0.82rem", color: "var(--color-text-muted)",
								}}
							>
								<IconArrowLeft size={14} /> Kembali
							</button>
							<h1 style={{ margin: "0 0 0.5rem", fontSize: "1.5rem", fontWeight: 800 }}>
								Ajak pasangan?
							</h1>
							<p style={{ margin: 0, fontSize: "0.88rem", color: "var(--color-text-muted)", lineHeight: 1.5 }}>
								Hubungkan dengan pasangan untuk fitur kuis harian bersama.
							</p>
						</div>

						{!hasCouple && !showCode && (
							<>
								<div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.75rem" }}>
									<div style={{
										width: 64, height: 64, borderRadius: "50%",
										background: "color-mix(in srgb, var(--color-primary) 12%, var(--color-surface-2))",
										display: "flex", alignItems: "center", justifyContent: "center",
									}}>
										<IconUserPlus size={28} color="var(--color-primary)" />
									</div>
									<p style={{ margin: 0, fontSize: "0.85rem", color: "var(--color-text-muted)", textAlign: "center", lineHeight: 1.5 }}>
										Buat kode undangan lalu bagikan ke pasanganmu.
									</p>
								</div>
								<Button
									onClick={handleGenerateCode}
									disabled={generating}
									style={{ opacity: generating ? 0.5 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "0.4rem" }}
								>
									{generating ? "Membuat..." : <><IconUserPlus size={16} /> Buat Kode Undangan</>}
								</Button>
							</>
						)}

						{!hasCouple && showCode && generatedCode && (
							<Card style={{ textAlign: "center", padding: "1.5rem 1rem" }}>
								<p style={{ margin: "0 0 0.75rem", fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
									Bagikan kode ini ke pasanganmu:
								</p>
								<div style={{
									fontSize: "1.8rem", fontWeight: 800, letterSpacing: "0.15em",
									fontFamily: "monospace", color: "var(--color-primary)",
									padding: "0.75rem", borderRadius: "var(--radius-lg)",
									background: "color-mix(in srgb, var(--color-primary) 8%, var(--color-surface))",
									marginBottom: "0.75rem",
								}}>
									{generatedCode}
								</div>
								<button
									type="button"
									onClick={() => { navigator.clipboard.writeText(generatedCode); toast.success("Kode tersalin!"); }}
									style={{
										padding: "0.4rem 0.8rem", borderRadius: "var(--radius-md)",
										border: "1px solid var(--color-border)", background: "var(--color-surface)",
										cursor: "pointer", fontSize: "0.8rem", fontWeight: 500, color: "var(--color-text)",
									}}
								>
									Salin Kode
								</button>
								<p style={{ margin: "0.75rem 0 0", fontSize: "0.78rem", color: "var(--color-text-muted)" }}>
									Menunggu pasangan bergabung...
								</p>
							</Card>
						)}

						{hasCouple && (
							<Card style={{ textAlign: "center", padding: "1.5rem 1rem" }}>
								<div style={{ display: "flex", justifyContent: "center", gap: "-0.5rem", marginBottom: "0.75rem" }}>
									<div style={{
										width: 48, height: 48, borderRadius: "50%",
										background: "var(--color-primary)", display: "flex",
										alignItems: "center", justifyContent: "center",
										fontSize: "1.2rem", color: "#fff", fontWeight: 700,
										border: "3px solid var(--color-surface)",
									}}>
										{(authName ?? "U").charAt(0).toUpperCase()}
									</div>
									<div style={{
										width: 48, height: 48, borderRadius: "50%",
										background: "var(--color-success)", display: "flex",
										alignItems: "center", justifyContent: "center",
										fontSize: "1.2rem", color: "#fff", fontWeight: 700,
										border: "3px solid var(--color-surface)",
									}}>
										💕
									</div>
								</div>
								<p style={{ margin: 0, fontSize: "0.9rem", fontWeight: 600 }}>
									{authName ?? "Kamu"} & {me?.couple?.user2?.nickname ?? "Pasangan"}
								</p>
								<p style={{ margin: "0.25rem 0 0", fontSize: "0.82rem", color: "var(--color-success)" }}>
									Terhubung! 🎉
								</p>
							</Card>
						)}

						<div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", alignItems: "center" }}>
							{!hasCouple && (
								<button
									type="button"
									onClick={() => { setJoinModalOpen(true); setJoinStep("input"); setJoinCode(""); setPartnerPreview(null); setJoinError(null); }}
									style={{
										padding: "0.5rem", border: "none", background: "transparent",
										cursor: "pointer", fontSize: "0.85rem", fontWeight: 500,
										color: "var(--color-primary)", textDecoration: "underline",
									}}
								>
									Sudah punya kode?
								</button>
							)}

							<button
								type="button"
								onClick={handleSkip}
								style={{
									padding: "0.75rem", borderRadius: "var(--radius-lg)",
									border: "none", background: "transparent",
									cursor: "pointer", fontSize: "0.88rem", fontWeight: 500,
									color: "var(--color-text-muted)",
								}}
							>
								Lewati dulu, nanti saja
							</button>
						</div>
					</div>
				)}
			</div>

			{/* Join Modal */}
			{joinModalOpen && (
				<div style={{
					position: "fixed", inset: 0, zIndex: 9999,
					display: "flex", alignItems: "center", justifyContent: "center",
					background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)",
					animation: "fadeIn 0.2s ease",
				}}>
					<div style={{
						background: "var(--color-surface)", borderRadius: "var(--radius-xl)",
						padding: "1.5rem", maxWidth: 360, width: "90%",
						boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
						animation: "slideUp 0.3s ease",
					}}>
						<div style={{ textAlign: "center", marginBottom: "1rem" }}>
							<h2 style={{ margin: "0 0 0.5rem", fontSize: "1.15rem", fontWeight: 700 }}>
								{joinStep === "input" ? "Masukkan Kode" : "Konfirmasi Pasangan"}
							</h2>
							{joinStep === "input" && (
								<p style={{ margin: 0, fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
									Masukkan kode 6 karakter dari pasanganmu.
								</p>
							)}
						</div>

						{joinStep === "input" && (
							<>
								<input
									type="text"
									placeholder="ABC123"
									value={joinCode}
									maxLength={6}
									onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
									autoFocus
									style={{
										width: "100%", padding: "0.75rem",
										borderRadius: "var(--radius-lg)",
										border: "2px solid var(--color-border)",
										background: "var(--color-surface)",
										fontSize: "1.2rem", fontWeight: 700,
										letterSpacing: "0.15em", textAlign: "center",
										fontFamily: "monospace", color: "var(--color-text)",
									}}
								/>
								{joinError && (
									<p style={{ margin: "0.5rem 0 0", fontSize: "0.78rem", color: "var(--color-error, #dc2626)", textAlign: "center" }}>
										{joinError}
									</p>
								)}
								<div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
									<button
										type="button"
										onClick={() => setJoinModalOpen(false)}
										style={{
											flex: 1, padding: "0.65rem", borderRadius: "var(--radius-lg)",
											border: "2px solid var(--color-border)", background: "var(--color-surface)",
											cursor: "pointer", fontSize: "0.88rem", fontWeight: 600,
											color: "var(--color-text)",
										}}
									>
										Batal
									</button>
									<button
										type="button"
										onClick={handleJoinLookup}
										disabled={joinCode.length !== 6 || joinLoading}
										style={{
											flex: 1, padding: "0.65rem", borderRadius: "var(--radius-lg)",
											border: "none", background: "var(--color-primary)",
											cursor: joinCode.length === 6 ? "pointer" : "not-allowed",
											fontSize: "0.88rem", fontWeight: 600, color: "#fff",
											opacity: joinCode.length === 6 ? 1 : 0.5,
										}}
									>
										{joinLoading ? "Memeriksa..." : "Lanjut"}
									</button>
								</div>
							</>
						)}

						{joinStep === "confirm" && partnerPreview && (
							<>
								<div style={{ display: "flex", justifyContent: "center", gap: "0.5rem", marginBottom: "1rem" }}>
									<div style={{
										width: 56, height: 56, borderRadius: "50%",
										background: "var(--color-primary)", display: "flex",
										alignItems: "center", justifyContent: "center",
										fontSize: "1.3rem", color: "#fff", fontWeight: 700,
										border: "3px solid var(--color-surface)",
									}}>
										{(authName ?? "U").charAt(0).toUpperCase()}
									</div>
									<div style={{
										width: 56, height: 56, borderRadius: "50%",
										background: "var(--color-success)", display: "flex",
										alignItems: "center", justifyContent: "center",
										fontSize: "1.3rem", color: "#fff", fontWeight: 700,
										border: "3px solid var(--color-surface)",
									}}>
										💕
									</div>
								</div>
								<div style={{ textAlign: "center", marginBottom: "1rem" }}>
									<p style={{ margin: 0, fontSize: "0.95rem", fontWeight: 600 }}>
										{authName ?? "Kamu"} & {partnerPreview.nickname ?? partnerPreview.name ?? "Pasangan"}
									</p>
									<p style={{ margin: "0.25rem 0 0", fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
										Pastikan ini pasangan yang tepat sebelum bergabung.
									</p>
								</div>
								{joinError && (
									<p style={{ margin: "0 0 0.75rem", fontSize: "0.78rem", color: "var(--color-error, #dc2626)", textAlign: "center" }}>
										{joinError}
									</p>
								)}
								<div style={{ display: "flex", gap: "0.5rem" }}>
									<button
										type="button"
										onClick={() => { setJoinStep("input"); setPartnerPreview(null); setJoinError(null); }}
										style={{
											flex: 1, padding: "0.65rem", borderRadius: "var(--radius-lg)",
											border: "2px solid var(--color-border)", background: "var(--color-surface)",
											cursor: "pointer", fontSize: "0.88rem", fontWeight: 600,
											color: "var(--color-text)",
										}}
									>
										Kembali
									</button>
									<button
										type="button"
										onClick={handleJoinConfirm}
										disabled={joinLoading}
										style={{
											flex: 1, padding: "0.65rem", borderRadius: "var(--radius-lg)",
											border: "none", background: "var(--color-success)",
											cursor: "pointer", fontSize: "0.88rem", fontWeight: 600,
											color: "#fff", opacity: joinLoading ? 0.5 : 1,
										}}
									>
										{joinLoading ? "Bergabung..." : "Ya, Gabung!"}
									</button>
								</div>
							</>
						)}

						<button
							type="button"
							onClick={() => setJoinModalOpen(false)}
							style={{
								position: "absolute", top: "0.75rem", right: "0.75rem",
								background: "none", border: "none", cursor: "pointer",
								fontSize: "1.2rem", color: "var(--color-text-muted)",
							}}
						>
							×
						</button>
					</div>
				</div>
			)}
		</div>
	);
}
